/* eslint-disable react-hooks/rules-of-hooks */

/* eslint-disable react-hooks/exhaustive-deps */
import { Menu, MenuButton, MenuItems } from "@headlessui/react";
import { AnimatePresence, motion } from "motion/react";

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { IconCheck } from "@/components/icons";
import { IconSeries, IconSkipNext } from "@/components/icons/player.icons";

import { ROUTES } from "@/config/routes";
import { useGetContentDetailPage, useGetContentEpisodes, useGetEpisodeInfo } from "@/hooks/api/content.hooks";
import { cn } from "@/utils/cn";

import { usePlayerControls2 } from "../hooks/PlayerControlsContext";

enum MenuTypes {
  MAIN = "MAIN",
  SEASONS = "seasons",
  EPISODES = "episodes",
}

const HandleSeries = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [menuType, setMenuType] = useState<MenuTypes>(MenuTypes.MAIN);
  const { id, episodeId } = useParams<{ id: string; episodeId: string }>();
  const [tempSeason, setTempSeason] = useState<number>(0);
  const { setControlsLock } = usePlayerControls2();
  const { t } = useTranslation();

  const contentId = Number(id);
  const currentEpisodeId = Number(episodeId);

  const { data: contentData } = useGetContentDetailPage(contentId);
  const isSerial = contentData?.content?.is_serial;

  const { data: episodeInfo } = useGetEpisodeInfo(currentEpisodeId, isSerial);

  const { data: episodeSeries, refetch } = useGetContentEpisodes(tempSeason);
  const { data: episodesData } = useGetContentEpisodes(episodeInfo?.season_id || 0);

  useEffect(() => {
    refetch();
  }, [tempSeason]);

  const episodes = episodesData?.episodes || [];

  const isEpisodePage = location.pathname.includes("/episode/");
  if (!isEpisodePage || !episodeInfo || !episodes.length) return null;

  const currentIndex = episodes.findIndex((ep) => ep.id === episodeInfo.id);
  const prevEpisode = episodes[currentIndex - 1];
  const nextEpisode = episodes[currentIndex + 1];

  const goToEpisode = (episodeId: number | undefined) => {
    if (!episodeId) return;
    navigate(ROUTES.DETAIL.SERIAL_EPISODE.replace(":id", String(id)).replace(":episodeId", String(episodeId)));
  };

  return (
    <div className="flex items-center gap-6 max-lg:gap-2">
      <div onClick={() => goToEpisode(prevEpisode?.id)}>
        <IconSkipNext className={cn("player-icon rotate-180", { "cursor-not-allowed opacity-20": !prevEpisode })} />
      </div>
      <div onClick={() => goToEpisode(nextEpisode?.id)}>
        <IconSkipNext className={cn("player-icon", { "cursor-not-allowed opacity-20": !nextEpisode })} />
      </div>
      <AnimatePresence mode="wait">
        <Menu as="div">
          {({ open, close }) => {
            const [hideTimer, setHideTimer] = useState<NodeJS.Timeout | null>(null);
            useEffect(() => {
              setControlsLock(!open);
            }, [open]);

            const handleMouseLeave = () => {
              const timer = setTimeout(() => {
                close();
              }, 2000);
              setHideTimer(timer);
            };

            const handleMouseEnter = () => {
              if (hideTimer) {
                clearTimeout(hideTimer);
                setHideTimer(null);
              }
            };
            return (
              <>
                <MenuButton
                  as="div"
                  onClick={() => {
                    setMenuType(MenuTypes.SEASONS);
                  }}
                >
                  <IconSeries className="player-icon !fill-transparent" />
                </MenuButton>
                <MenuItems
                  className={"absolute bottom-[100%] right-0 z-[100] mb-[50px] w-[304px] outline-none max-lg:w-[200px]"}
                  as="div"
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                >
                  <motion.div
                    key={menuType}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    transition={{ duration: 0.3 }}
                    className="space-y-2 rounded-2xl bg-surface py-3 max-lg:py-2"
                  >
                    {MenuTypes.SEASONS == menuType && (
                      <>
                        <div className="flex items-center justify-end border-b border-outline px-4 py-2">
                          <p className="text-[18px] text-white underline">{t("player.seasons")}</p>
                        </div>
                        {contentData.content.seasons?.map((e) => (
                          <div
                            className="flex cursor-pointer items-center justify-start gap-2 border-b border-outline px-4 py-2 transition-colors hover:bg-surface-container max-lg:py-[4px]"
                            onClick={() => {
                              setMenuType(MenuTypes.EPISODES);
                              setTempSeason(e.id);
                            }}
                          >
                            <IconCheck
                              className={cn("size-6 gap-2 stroke-white opacity-0", {
                                "opacity-100": e.id === episodeInfo.season_id,
                              })}
                            />
                            <p className="text-[20px]/8 font-semibold text-white max-lg:text-[16px]/normal ">
                              {" "}
                              {e.season_numb} {t("player.season")}
                            </p>
                          </div>
                        ))}
                      </>
                    )}
                    {MenuTypes.EPISODES == menuType && (
                      <>
                        <div className="flex items-center justify-between overflow-auto border-b border-outline px-4 py-2 max-lg:py-[4px]">
                          <p className="text-[18px] text-[#808080]">
                            {contentData.content.seasons?.find((e) => e.id === tempSeason)?.season_numb}{" "}
                            {t("player.season")}
                          </p>
                          <p
                            className="cursor-pointer text-[18px] text-white underline"
                            onClick={() => setMenuType(MenuTypes.SEASONS)}
                          >
                            {t("player.seasons")}
                          </p>
                        </div>
                        <div className="max-h-[390px] overflow-y-scroll">
                          {episodeSeries?.episodes?.map((e) => (
                            <div
                              className="flex cursor-pointer items-center justify-start gap-2 border-b border-outline px-4 py-2 transition-colors hover:bg-surface-container max-lg:py-[4px]"
                              onClick={() => {
                                navigate(
                                  ROUTES.DETAIL.SERIAL_EPISODE.replace(":id", String(id)).replace(
                                    ":episodeId",
                                    String(e.id),
                                  ),
                                );
                              }}
                            >
                              <IconCheck
                                className={cn("size-6 gap-2 stroke-white opacity-0", {
                                  "opacity-100": episodeId && +episodeId == +e.id,
                                })}
                              />
                              <p className="text-[20px]/8 font-semibold text-white max-lg:text-[16px]/normal ">
                                {" "}
                                {e.episode_numb} {t("player.episode")}
                              </p>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </motion.div>
                </MenuItems>
              </>
            );
          }}
        </Menu>
      </AnimatePresence>
    </div>
  );
};

export default HandleSeries;
